import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import Navbar from './Navbar';
import BillingMenu, { DRAWER_WIDTH as BILLING_DRAWER_WIDTH } from '../billing/BillingMenu';
import Sidebar, { DRAWER_WIDTH as SIDEBAR_DRAWER_WIDTH } from './Sidebar';
import MobileNav from './MobileNav';
import MobileHeader, { MOBILE_HEADER_HEIGHT } from './MobileHeader';

const MainLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  // Obtener módulos autorizados
  const getAuthorizedModules = () => {
    try {
      const stored = localStorage.getItem('nx2026_authorized_modules');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const authorizedModules = getAuthorizedModules();
  const hasFacturacion = authorizedModules.includes('FACTURACION');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Rutas donde mostrar botón de filtros
  const showFilterInHeader = location.pathname.startsWith('/pos');
  
  // Ocultar sidebar en rutas de billing (tienen su propio menú)
  // o si no tiene acceso a facturación
  const isBillingRoute = location.pathname.startsWith('/billing');
  
  // Mostrar BillingMenu solo en rutas de billing
  const showBillingMenu = hasFacturacion && isBillingRoute;
  
  // Mostrar Sidebar regular en otras rutas (panel, pos, settings, etc.)
  const showRegularSidebar = !isBillingRoute;

  // Determinar el item seleccionado en el menú de facturación
  const getSelectedItem = () => {
    const path = location.pathname;
    if (path.includes('consumidor-final')) return 'consumidor-final';
    if (path.includes('/ccf')) return 'ccf';
    if (path.includes('retencion')) return 'retencion';
    if (path.includes('sujeto-excluido')) return 'sujeto-excluido';
    if (path.includes('nota-credito')) return 'nota-credito';
    if (path.includes('nota-debito')) return 'nota-debito';
    if (path.includes('libros-ventas')) return 'libros-ventas';
    if (path.includes('libro-ventas')) return 'libros-ventas';
    if (path.includes('libros-compras')) return 'libros-compras';
    if (path.includes('libro-compras')) return 'libros-compras';
    if (path.includes('anexos-f07')) return 'anexos-f07';
    if (path.includes('anexos-f14')) return 'anexos-f07';
    if (path.includes('anexos-f930')) return 'anexos-f07';
    if (path.includes('compras-cf')) return 'libros-compras';
    if (path.includes('compras-ccf')) return 'libros-compras';
    if (path.includes('libro-ventas-cf')) return 'libros-ventas';
    if (path.includes('libro-ventas-ccf')) return 'libros-ventas';
    if (path.includes('f07-iva')) return 'anexos-f07';
    if (path.includes('f14-pcta')) return 'anexos-f07';
    return 'consumidor-final';
  };

  const handleSelectItem = (itemId) => {
    navigate(`/billing/${itemId}`);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleMobileClose = () => {
    setMobileOpen(false);
  };

  // Determinar el ancho del drawer según el tipo de sidebar
  const drawerWidth = showBillingMenu ? BILLING_DRAWER_WIDTH : (showRegularSidebar ? SIDEBAR_DRAWER_WIDTH : 0);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Navbar (siempre visible en desktop, oculta en rutas billing en desktop) */}
      <Navbar onMobileMenuClick={handleDrawerToggle} />
      
      {/* Mobile Header (solo visible en móvil) */}
      <MobileHeader 
        onMenuClick={handleDrawerToggle}
        showFilter={showFilterInHeader}
      />
      
      {/* Sidebar con BillingMenu para desktop - solo mostrar si tiene acceso a facturación */}
      {showBillingMenu && (
        <Box
          component="nav"
          sx={{ 
            width: { md: drawerWidth }, 
            flexShrink: { md: 0 },
            display: { xs: 'none', md: isBillingRoute ? 'none' : 'block' }
          }}
        >
          {/* Drawer móvil */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: BILLING_DRAWER_WIDTH,
                backgroundColor: '#ffffff',
                color: 'white',
              },
            }}
          >
            {/* Usar BillingMenu para el drawer móvil */}
            <Box sx={{ overflow: 'auto', pt: 0 }}>
              <BillingMenu 
                selectedItem={getSelectedItem()}
                onSelectItem={handleSelectItem}
                mobileOpen={isMobile && mobileOpen}
                onMobileClose={handleMobileClose}
                isMobile={isMobile}
              />
            </Box>
          </Drawer>

          {/* BillingMenu permanente para desktop */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: BILLING_DRAWER_WIDTH,
                backgroundColor: '#ffffff',
                color: 'white',
                borderRight: '1px solid rgba(255,255,255,0.1)',
              },
            }}
            open
          >
            <BillingMenu 
              selectedItem={getSelectedItem()}
              onSelectItem={handleSelectItem}
              mobileOpen={false}
              onMobileClose={handleMobileClose}
              isMobile={false}
            />
          </Drawer>
        </Box>
      )}

      {/* Sidebar regular para rutas no-billing (panel, pos, settings, etc.) */}
      {showRegularSidebar && (
        <Box
          component="nav"
          sx={{ 
            width: { md: drawerWidth }, 
            flexShrink: { md: 0 },
            display: { xs: 'none', md: 'block' }
          }}
        >
          {/* Drawer móvil */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: SIDEBAR_DRAWER_WIDTH,
              },
            }}
          >
            <Box sx={{ overflow: 'auto', pt: 0 }}>
              <Sidebar isMobileDrawer={true} />
            </Box>
          </Drawer>

          {/* Sidebar permanente para desktop */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: SIDEBAR_DRAWER_WIDTH,
              },
            }}
            open
          >
            <Sidebar />
          </Drawer>
        </Box>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: isMobile ? `${MOBILE_HEADER_HEIGHT}px` : (isBillingRoute ? 0 : '64px'),
          ml: isMobile ? 0 : (isBillingRoute ? 0 : drawerWidth),
          width: isMobile ? '100%' : (isBillingRoute ? '100%' : `calc(100% - ${drawerWidth}px)`),
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
          pb: isMobile ? '80px' : 3,
          // Override directo para eliminar padding en rutas de billing
          ...(isBillingRoute && {
            ml: '0 !important',
            width: '100% !important',
          }),
        }}
      >
        <Outlet />
      </Box>

      {/* Barra de navegación inferior para móvil */}
      {isMobile && <MobileNav />}
    </Box>
  );
};

export default MainLayout;
